import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

// Organization JSON-LD — static, injected on every page for maximum LLM and rich-result coverage
const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'CINBIOSIS',
  url: 'https://cinbiosis.com',
  logo: 'https://cinbiosis.com/logo.png',
  description:
    'CINBIOSIS — Consultoría ambiental en Colombia. Muestreo biológico, licenciamiento ANLA, sistemas MRV, inventarios GEI (ISO 14064, IPCC) y fotogrametría UAV para proyectos de infraestructura y medio ambiente.',
  email: 'ventas@cinbiosis.com',
  telephone: '+573001234567',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'CO',
  },
  areaServed: 'Colombia',
  knowsAbout: [
    'Muestreo ambiental',
    'Licenciamiento ambiental',
    'Sistemas MRV',
    'Inventarios GEI',
    'Fotogrametría UAV',
    'Servicios geoespaciales',
    'Gestión de cambio climático',
    'ISO 14064',
    'IPCC',
    'GHG Protocol',
  ],
  sameAs: [],
};

/**
 * Reusable SEO component utilizing react-helmet-async.
 * Injects <head> tags for proper crawler indexing, internationalization,
 * Open Graph/Twitter social sharing, and JSON-LD structured data.
 */
const SEO = ({ title, description, canonicalPath, jsonLd }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language; // 'es' or 'en'

  const baseUrl = 'https://cinbiosis.com';
  const ogImage = `${baseUrl}/og-image.jpg`;

  const esUrl = `${baseUrl}/es${canonicalPath === '/' ? '' : canonicalPath}`;
  const enUrl = `${baseUrl}/en${canonicalPath === '/' ? '' : canonicalPath}`;
  const currentUrl = currentLang === 'en' ? enUrl : esUrl;

  // Merge the global Organization schema with any page-specific JSON-LD
  const schemas = [ORGANIZATION_SCHEMA, ...(jsonLd ? [jsonLd] : [])];

  return (
    <Helmet>
      {/* ── Basic Metadata ── */}
      <html lang={currentLang} />
      <title>{title}</title>
      <meta name="description" content={description} />

      {/* ── Canonical + hreflang ── */}
      <link rel="canonical" href={currentUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="x-default" href={esUrl} />

      {/* ── Open Graph ── */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CINBIOSIS" />
      <meta property="og:locale" content={currentLang === 'en' ? 'en_US' : 'es_CO'} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="CINBIOSIS — Consultoría y Muestreo Ambiental" />

      {/* ── Twitter / X Card ── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content="CINBIOSIS — Consultoría y Muestreo Ambiental" />

      {/* ── JSON-LD Structured Data (Organization + page-specific) ── */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
